import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Card,
  Spin,
  Button,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Tag,
  message,
  Alert
} from "antd";
import {
  FaDownload,
  FaPrint,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendar,
  FaSchool,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaBox
} from "react-icons/fa";
import "./ReportGenerator.css";

const { Title, Text, Paragraph } = Typography;

const ReportViewer = () => {
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const token = searchParams.get('token');
      const headers = { 'x-api-key': process.env.REACT_APP_SCHOOL_API_KEY };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(
        `${process.env.REACT_APP_GREENFIELD_URL}/api/installation-report/${reportId}`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error('Report not found');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setReport(result.data);
      } else {
        throw new Error('Failed to load report');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err.message);
      message.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Spin size="large" tip="Loading report..." />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <Alert
          message="Error"
          description={error || "Report not found"}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="installation-report-container">
      {/* Print/Download Actions - Hidden in print */}
      <div className="no-print" style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        background: 'white',
        padding: '16px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Installation Report
              </Title>
            </Col>
            <Col>
              <Space>
                <Button 
                  icon={<FaPrint />} 
                  onClick={handlePrint}
                >
                  Print
                </Button>
                <Button 
                  type="primary" 
                  icon={<FaDownload />}
                  onClick={handleDownloadPDF}
                >
                  Download PDF
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      </div>

      {/* Report Content */}
      <div ref={printRef} style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
        {/* Header Section with Lead Group and Date - Less prominent */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 16 
        }}>
          <div style={{
            color: '#666',
            fontSize: 16,
            fontWeight: 500
          }}>
            LEAD Group
          </div>
          <div style={{
            color: '#666',
            fontSize: 16,
            fontWeight: 500
          }}>
            {new Date(report.timestamp || report.createdAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}, {new Date(report.timestamp || report.createdAt).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }).toLowerCase()}
          </div>
        </div>

        {/* Installation Report Header - Main Title */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: 32,
          padding: '24px 0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 8,
          color: 'white'
        }}>
          <Title level={1} style={{ 
            color: 'white', 
            marginTop: 0, 
            marginBottom: 0,
            fontWeight: 700,
            fontSize: 32
          }}>
            Installation Report
          </Title>
        </div>

        {/* School Information */}
        <Card 
          title={
            <Space>
              <FaSchool />
              <span>School Information</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Text type="secondary">Sales Order NO:</Text>
              <div><Text strong>{report.salesOrderNo}</Text></div>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">School Name:</Text>
              <div><Text strong>{report.schoolName}</Text></div>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Total TV & IFPD Installed Qty:</Text>
              <div><Text strong>{report.totalIFPQty}</Text></div>
            </Col>
            <Col xs={24}>
              <Text type="secondary">School Address:</Text>
              <div><Text strong>{report.schoolAddress}</Text></div>
            </Col>
          </Row>
        </Card>

        {/* Serial Numbers */}
        <Card
          title={
            <Space>
              <FaBox />
              <span>TV & IFPD Serial Numbers ({report.serialNumbers ? report.serialNumbers.filter(item => 
                (typeof item === 'object' && item.installationStatus === 'installed') || 
                (typeof item === 'string')
              ).length : 0}/{report.serialNumbers ? report.serialNumbers.length : 0})</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {report.serialNumbers && report.serialNumbers.map((item, index) => {
              const isInstalled = typeof item === 'object' ? item.installationStatus === 'installed' : true;
              const deviceType = typeof item === 'object' ? item.deviceType : 'Unknown';
              const serial = typeof item === 'string' ? item : item.serial;
              
              return (
                <Card key={index} size="small" style={{ 
                  background: isInstalled ? '#fafafa' : '#fff2e8',
                  border: `1px solid ${isInstalled ? '#d9d9d9' : '#ffbb96'}`
                }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24}>
                      <Space>
                        <Tag color="blue">#{index + 1}</Tag>
                        <Text strong>{serial}</Text>
                        {typeof item === 'object' && (
                          <>
                            <Tag color={deviceType === 'TV' ? 'green' : 'purple'}>
                              {deviceType}
                            </Tag>
                            <Tag color={isInstalled ? 'success' : 'warning'}>
                              {isInstalled ? 'Installed' : 'Not Installed'}
                            </Tag>
                          </>
                        )}
                      </Space>
                    </Col>
                    
                    {/* QC Checks for installed devices */}
                    {typeof item === 'object' && item.qcChecks && isInstalled && (
                      <Col xs={24}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          QC Checks ({deviceType}):
                        </Text>
                        <Row gutter={[8, 8]}>
                          {item.qcChecks.checks.map((check, checkIndex) => (
                            <Col xs={24} sm={12} key={checkIndex}>
                              <div style={{
                                padding: 6,
                                background: check.status === 'passed' ? '#f6ffed' : '#fff2e8',
                                border: `1px solid ${check.status === 'passed' ? '#b7eb8f' : '#ffbb96'}`,
                                borderRadius: 4,
                                fontSize: 12
                              }}>
                                <Space>
                                  {check.status === 'passed' ? (
                                    <FaCheckCircle style={{ color: '#52c41a' }} />
                                  ) : (
                                    <FaTimesCircle style={{ color: '#ff4d4f' }} />
                                  )}
                                  <Text style={{ fontSize: 12 }}>{check.name}</Text>
                                </Space>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </Col>
                    )}
                    
                    {/* Accessories for installed devices */}
                    {typeof item === 'object' && item.accessories && isInstalled && (
                      <Col xs={24}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          Accessories ({deviceType}):
                        </Text>
                        <Space wrap>
                          {Object.entries(item.accessories).map(([key, value]) => (
                            <Tag 
                              key={key} 
                              color={value ? 'success' : 'error'}
                              icon={value ? <FaCheckCircle /> : <FaTimesCircle />}
                              style={{ fontSize: 11 }}
                            >
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </Tag>
                          ))}
                        </Space>
                      </Col>
                    )}
                  </Row>
                </Card>
              );
            })}
          </Space>
        </Card>

        {/* Engineer Information */}
        <Card
          title={
            <Space>
              <FaUser />
              <span>Engineer Information</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Text type="secondary">Engineer Name:</Text>
              <div><Text strong>{report.engineerName}</Text></div>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Engineer Contact:</Text>
              <div>
                <Space>
                  <FaPhone />
                  <Text strong>{report.engineerContact}</Text>
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Signature */}
        <Card
          title="Signature"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
                  {report.signaturePerson || report.spocName || 'N/A'}
                </div>
                <div style={{ marginBottom: 8, fontSize: 14, color: '#666' }}>
                  {report.signatureRole || report.spocRole || 'N/A'}
                </div>
                <div style={{ fontSize: 14, color: '#666' }}>
                  <Space>
                    <FaPhone />
                    <span>{report.mobileNumber}</span>
                  </Space>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{
                padding: 12,
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                background: 'white',
                textAlign: 'center',
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {report.signatureData || report.digitalSignature ? (
                  <img
                    src={report.signatureData || report.digitalSignature}
                    alt="Digital Signature"
                    style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
                  />
                ) : (
                  <Text type="secondary">No signature available</Text>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Footer */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">
              Report ID: {report._id}
            </Text>
            <br />
            <Text type="secondary">
              Submitted on: {new Date(report.timestamp || report.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}, {new Date(report.timestamp || report.createdAt).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              }).toLowerCase()}
            </Text>
          </div>
        </Card>
      </div>

      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          .installation-report-container {
            background: white !important;
          }
          
          .ant-card {
            page-break-inside: avoid;
            box-shadow: none !important;
            border: 1px solid #ddd !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReportViewer;
